"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { CreateDropWaveWrapper } from "@/components/waves/CreateDropWaveWrapper";
import { WaveDropsAllWithoutProvider } from "@/components/waves/drops/wave-drops-all";
import { WaveGallery } from "@/components/waves/gallery";
import MobileMemesArtSubmissionBtn from "@/components/waves/memes/submission/MobileMemesArtSubmissionBtn";
import PrivilegedDropCreator, { DropMode } from "@/components/waves/PrivilegedDropCreator";
import { useNotificationsContext } from "@/components/notifications/NotificationsContext";
import { ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { UnreadDividerProvider, useUnreadDivider } from "@/contexts/wave/UnreadDividerContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiWave } from "@/generated/models/ApiWave";
import { getHomeRoute } from "@/helpers/navigation.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { resolveWaveSubmissionExperience, WaveSubmissionExperience } from "@/helpers/waves/wave-submission-experience.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useWave } from "@/hooks/useWave";
import { useApprovalWaveStatus } from "@/hooks/waves/useApprovalWaveStatus";
import type { WaveViewMode } from "@/hooks/useWaveViewMode";
import { selectEditingDropId } from "@/store/editSlice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { ActiveDropAction } from "@/types/dropInteractionTypes";
import { commonApiPostWithoutBodyAndResponse } from "@/services/api/common-api";
import { ACCEPTED_FILE_TYPE_LABELS, isSupportedUploadFile } from "@/services/uploads/mediaUploadMimeType";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLayout } from "./layout/LayoutContext";
interface InitialDropState {
  readonly waveId: string;
  readonly serialNo: number;
  readonly dividerSerialNo: number | null;
}
interface MyStreamWaveChatProps {
  readonly wave: ApiWave;
  readonly firstUnreadSerialNo: number | null;
  readonly viewMode: WaveViewMode;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
interface WaveChatLeaveHandlerProps {
  readonly enabled: boolean;
  readonly waveId: string;
}
const MAX_UNSUPPORTED_FILE_NAMES_IN_TOAST = 3;
const WaveChatLeaveHandler: React.FC<WaveChatLeaveHandlerProps> = t0 => {
  const $ = _c(7);
  const {
    enabled,
    waveId
  } = t0;
  const {
    setUnreadDividerSerialNo
  } = useUnreadDivider();
  const {
    removeWaveDeliveredNotifications
  } = useNotificationsContext();
  const {
    invalidateNotifications
  } = useContext(ReactQueryWrapperContext);
  let t1;
  let t2;
  if ($[0] !== enabled || $[1] !== invalidateNotifications || $[2] !== removeWaveDeliveredNotifications || $[3] !== setUnreadDividerSerialNo || $[4] !== waveId) {
    t1 = () => {
      if (!enabled) {
        return;
      }
      return () => {
        setUnreadDividerSerialNo(null);
        (async () => {
          if (document.visibilityState !== "visible") {
            return;
          }
          ;
          try {
            await Promise.resolve(removeWaveDeliveredNotifications(waveId));
          } catch (t3) {
            const error = t3;
            console.error("Failed to remove wave delivered notifications:", error);
          }
          ;
          try {
            await commonApiPostWithoutBodyAndResponse({
              endpoint: `notifications/wave/${waveId}/read`
            });
            invalidateNotifications();
          } catch (t4) {
            const error_0 = t4;
            console.error("Failed to mark feed as read:", error_0);
          }
        })();
      };
    };
    t2 = [enabled, waveId, setUnreadDividerSerialNo, removeWaveDeliveredNotifications, invalidateNotifications];
    $[0] = enabled;
    $[1] = invalidateNotifications;
    $[2] = removeWaveDeliveredNotifications;
    $[3] = setUnreadDividerSerialNo;
    $[4] = waveId;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  return null;
};
const MyStreamWaveChat: React.FC<MyStreamWaveChatProps> = t0 => {
  const $ = _c(91);
  const {
    wave,
    firstUnreadSerialNo,
    viewMode,
    onDropClick
  } = t0;
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const galleryContainerRef = useRef(null);
  const dragCounterRef = useRef(0);
  const externalAttachmentDropTokenRef = useRef(0);
  const {
    connectedProfile,
    setToast
  } = useAuth();
  const {
    isMemesWave,
    isCurationWave,
    isQuorumWave
  } = useWave(wave);
  const t1 = wave.participation.submission_strategy ?? null;
  let t2;
  if ($[0] !== isCurationWave || $[1] !== isMemesWave || $[2] !== isQuorumWave || $[3] !== t1) {
    t2 = resolveWaveSubmissionExperience({
      isMemesWave,
      isCurationWave,
      isQuorumWave,
      submissionStrategy: t1
    });
    $[0] = isCurationWave;
    $[1] = isMemesWave;
    $[2] = isQuorumWave;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const submissionExperience = t2;
  const editingDropId = useSelector(selectEditingDropId);
  const {
    isApp
  } = useDeviceInfo();
  const [isDragDropActive, setIsDragDropActive] = useState(false);
  const [externalAttachmentDrop, setExternalAttachmentDrop] = useState(null);
  let t3;
  if ($[5] !== wave.id) {
    t3 = {
      waveId: wave.id,
      activeDrop: null
    };
    $[5] = wave.id;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const [activeDropState, setActiveDropState] = useState(t3);
  const activeDrop = activeDropState.waveId === wave.id ? activeDropState.activeDrop : null;
  let t4;
  if ($[7] !== wave.id) {
    t4 = nextActiveDrop => {
      setActiveDropState({
        waveId: wave.id,
        activeDrop: nextActiveDrop
      });
    };
    $[7] = wave.id;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const setActiveDropForWave = t4;
  let t5;
  if ($[9] !== firstUnreadSerialNo || $[10] !== searchParams || $[11] !== wave.id) {
    bb0: {
      const dropParam = searchParams.get("serialNo");
      if (!dropParam) {
        t5 = null;
        break bb0;
      }
      const parsed = Number.parseInt(dropParam, 10);
      if (!Number.isFinite(parsed)) {
        t5 = null;
        break bb0;
      }
      let t6;
      if ($[13] !== firstUnreadSerialNo || $[14] !== searchParams) {
        const dividerParam = searchParams.get("divider");
        const dividerParsed = dividerParam ? Number.parseInt(dividerParam, 10) : null;
        t6 = dividerParsed !== null && Number.isFinite(dividerParsed) ? dividerParsed : firstUnreadSerialNo;
        $[13] = firstUnreadSerialNo;
        $[14] = searchParams;
        $[15] = t6;
      } else {
        t6 = $[15];
      }
      const dividerSerialNo = t6;
      let t7;
      if ($[16] !== dividerSerialNo || $[17] !== parsed || $[18] !== wave.id) {
        t7 = {
          waveId: wave.id,
          serialNo: parsed,
          dividerSerialNo
        };
        $[16] = dividerSerialNo;
        $[17] = parsed;
        $[18] = wave.id;
        $[19] = t7;
      } else {
        t7 = $[19];
      }
      t5 = t7;
    }
    $[9] = firstUnreadSerialNo;
    $[10] = searchParams;
    $[11] = wave.id;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const initialDropState = t5;
  let t6;
  if ($[20] !== firstUnreadSerialNo || $[21] !== initialDropState || $[22] !== wave.id) {
    t6 = () => {
      if (initialDropState?.waveId === wave.id && initialDropState.dividerSerialNo !== null) {
        return {
          waveId: wave.id,
          serialNo: initialDropState.dividerSerialNo
        };
      }
      return {
        waveId: wave.id,
        serialNo: firstUnreadSerialNo
      };
    };
    $[20] = firstUnreadSerialNo;
    $[21] = initialDropState;
    $[22] = wave.id;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const [capturedDividerState] = useState(t6);
  const scrollTarget = initialDropState?.waveId === wave.id ? initialDropState.serialNo : null;
  let dividerTarget = firstUnreadSerialNo;
  if (initialDropState?.waveId === wave.id) {
    dividerTarget = initialDropState.dividerSerialNo;
  } else {
    if (capturedDividerState.waveId === wave.id) {
      dividerTarget = capturedDividerState.serialNo;
    }
  }
  let t7;
  let t8;
  if ($[24] !== initialDropState || $[25] !== pathname || $[26] !== router || $[27] !== searchParams || $[28] !== viewMode) {
    t7 = () => {
      if (!initialDropState || viewMode === "gallery") {
        return;
      }
      const params = new URLSearchParams(searchParams.toString() || "");
      if (!params.has("serialNo") && !params.has("divider")) {
        return;
      }
      params.delete("serialNo");
      params.delete("divider");
      const href = params.toString() ? `${pathname}?${params.toString()}` : pathname || getHomeRoute();
      router.replace(href, {
        scroll: false
      });
    };
    t8 = [initialDropState, pathname, router, searchParams, viewMode];
    $[24] = initialDropState;
    $[25] = pathname;
    $[26] = router;
    $[27] = searchParams;
    $[28] = viewMode;
    $[29] = t7;
    $[30] = t8;
  } else {
    t7 = $[29];
    t8 = $[30];
  }
  useEffect(t7, t8);
  const {
    waveViewStyle
  } = useLayout();
  let t9;
  if ($[31] !== setActiveDropForWave) {
    const onReply = (drop, partId) => {
      setActiveDropForWave({
        action: ActiveDropAction.REPLY,
        drop,
        partId
      });
    };
    t9 = t10 => {
      const {
        drop: drop_0,
        partId: partId_0
      } = t10;
      onReply(drop_0, partId_0);
    };
    $[31] = setActiveDropForWave;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const handleReply = t9;
  let t10;
  if ($[33] !== setActiveDropForWave) {
    t10 = () => {
      setActiveDropForWave(null);
    };
    $[33] = setActiveDropForWave;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  const onCancelReplyQuote = t10;
  let t11;
  if ($[35] !== wave) {
    t11 = {
      wave
    };
    $[35] = wave;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  const {
    winningThreshold,
    isVotingClosed,
    isVotingControlsLocked
  } = useApprovalWaveStatus(t11);
  const fixedDropMode = isVotingControlsLocked ? DropMode.CHAT : DropMode.BOTH;
  const shouldHandleContainerFileDrop = _temp;
  const isFileDragEvent = _temp2;
  const filterSupportedFiles = _temp3;
  let t12;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      dragCounterRef.current = 0;
      setIsDragDropActive(false);
    };
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  const resetDragDropState = t12;
  let t13;
  let t14;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      globalThis.window.addEventListener("dragend", resetDragDropState);
      globalThis.window.addEventListener("drop", resetDragDropState);
      return () => {
        globalThis.window.removeEventListener("dragend", resetDragDropState);
        globalThis.window.removeEventListener("drop", resetDragDropState);
      };
    };
    t14 = [resetDragDropState];
    $[38] = t13;
    $[39] = t14;
  } else {
    t13 = $[38];
    t14 = $[39];
  }
  useEffect(t13, t14);
  let t15;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = event_1 => {
      if (!isFileDragEvent(event_1) || !shouldHandleContainerFileDrop(event_1)) {
        return;
      }
      event_1.preventDefault();
      event_1.stopPropagation();
      dragCounterRef.current = dragCounterRef.current + 1;
      setIsDragDropActive(true);
    };
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  const onContainerDragEnter = t15;
  let t16;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = event_2 => {
      if (!isFileDragEvent(event_2) || !shouldHandleContainerFileDrop(event_2)) {
        return;
      }
      event_2.preventDefault();
      event_2.stopPropagation();
      event_2.dataTransfer.dropEffect = "copy";
    };
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  const onContainerDragOver = t16;
  let t17;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = event_3 => {
      if (!isFileDragEvent(event_3) || !shouldHandleContainerFileDrop(event_3)) {
        return;
      }
      event_3.preventDefault();
      event_3.stopPropagation();
      dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
      if (dragCounterRef.current === 0) {
        setIsDragDropActive(false);
      }
    };
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  const onContainerDragLeave = t17;
  let onContainerDrop;
  if ($[43] !== setToast) {
    onContainerDrop = event_4 => {
      if (!isFileDragEvent(event_4) || !shouldHandleContainerFileDrop(event_4)) {
        return;
      }
      event_4.preventDefault();
      event_4.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragDropActive(false);
      const droppedFiles = Array.from(event_4.dataTransfer.files ?? []);
      if (droppedFiles.length === 0) {
        return;
      }
      handleExternalAttachmentFiles(droppedFiles);
    };
    const formatUnsupportedFileNames = _temp5;
    const handleExternalAttachmentFiles = files_0 => {
      if (files_0.length === 0) {
        return;
      }
      const {
        supported: supported_0,
        unsupported: unsupported_1
      } = filterSupportedFiles(files_0);
      if (supported_0.length > 0) {
        externalAttachmentDropTokenRef.current = externalAttachmentDropTokenRef.current + 1;
        setExternalAttachmentDrop({
          token: externalAttachmentDropTokenRef.current,
          files: supported_0
        });
      }
      if (unsupported_1.length > 0) {
        const unsupportedNames = formatUnsupportedFileNames(unsupported_1);
        setToast({
          message: `Unsupported file type: ${unsupportedNames}. Accepted Types: ${ACCEPTED_FILE_TYPE_LABELS}`,
          type: "error"
        });
      }
    };
    $[43] = setToast;
    $[44] = onContainerDrop;
  } else {
    onContainerDrop = $[44];
  }
  if (viewMode === "gallery") {
    let t18;
    if ($[45] !== onDropClick || $[46] !== wave) {
      t18 = <WaveGallery wave={wave} onDropClick={onDropClick} />;
      $[45] = onDropClick;
      $[46] = wave;
      $[47] = t18;
    } else {
      t18 = $[47];
    }
    let t19;
    if ($[48] !== t18 || $[49] !== waveViewStyle) {
      t19 = <div ref={galleryContainerRef} className="tw-flex tw-h-full tw-w-full tw-flex-col tw-overflow-hidden" style={waveViewStyle}>{t18}</div>;
      $[48] = t18;
      $[49] = waveViewStyle;
      $[50] = t19;
    } else {
      t19 = $[50];
    }
    return t19;
  }
  const t18 = dividerTarget ?? null;
  const t19 = `unread-divider-${wave.id}`;
  const t20 = Boolean(connectedProfile?.handle);
  let t21;
  if ($[51] !== t20 || $[52] !== wave.id) {
    t21 = <WaveChatLeaveHandler enabled={t20} waveId={wave.id} />;
    $[51] = t20;
    $[52] = wave.id;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== isDragDropActive) {
    t22 = isDragDropActive && <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-40 tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border-2 tw-border-dotted tw-border-primary-400 tw-bg-iron-900/75 tw-p-6"><div className="tw-max-w-3xl tw-p-4 tw-text-center"><p className="tw-text-base tw-font-semibold tw-text-primary-300">Drop files here</p><p className="tw-mt-2 tw-text-xs tw-text-iron-300">Accepted types: {ACCEPTED_FILE_TYPE_LABELS}</p></div></div>;
    $[54] = isDragDropActive;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== activeDrop || $[57] !== dividerTarget || $[58] !== handleReply || $[59] !== isVotingClosed || $[60] !== isVotingControlsLocked || $[61] !== scrollTarget || $[62] !== wave.id || $[63] !== wave.metrics.muted || $[64] !== wave.metrics.your_unread_drops_count || $[65] !== winningThreshold) {
    t23 = <WaveDropsAllWithoutProvider key={wave.id} waveId={wave.id} onReply={handleReply} activeDrop={activeDrop} initialDrop={scrollTarget} dividerSerialNo={dividerTarget} unreadCount={wave.metrics.your_unread_drops_count} dropId={null} isMuted={wave.metrics.muted} winningThreshold={winningThreshold} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} />;
    $[56] = activeDrop;
    $[57] = dividerTarget;
    $[58] = handleReply;
    $[59] = isVotingClosed;
    $[60] = isVotingControlsLocked;
    $[61] = scrollTarget;
    $[62] = wave.id;
    $[63] = wave.metrics.muted;
    $[64] = wave.metrics.your_unread_drops_count;
    $[65] = winningThreshold;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  let t24;
  if ($[67] !== activeDrop || $[68] !== editingDropId || $[69] !== externalAttachmentDrop || $[70] !== fixedDropMode || $[71] !== isApp || $[72] !== onCancelReplyQuote || $[73] !== wave) {
    t24 = !(isApp && editingDropId) && <div className="tw-mt-auto"><CreateDropWaveWrapper><PrivilegedDropCreator activeDrop={activeDrop} onCancelReplyQuote={onCancelReplyQuote} onDropAddedToQueue={onCancelReplyQuote} wave={wave} dropId={null} fixedDropMode={fixedDropMode} externalAttachmentDrop={externalAttachmentDrop} onExternalAttachmentDropConsumed={() => setExternalAttachmentDrop(null)} /></CreateDropWaveWrapper></div>;
    $[67] = activeDrop;
    $[68] = editingDropId;
    $[69] = externalAttachmentDrop;
    $[70] = fixedDropMode;
    $[71] = isApp;
    $[72] = onCancelReplyQuote;
    $[73] = wave;
    $[74] = t24;
  } else {
    t24 = $[74];
  }
  let t25;
  if ($[75] !== isVotingControlsLocked || $[76] !== submissionExperience || $[77] !== wave) {
    t25 = submissionExperience === WaveSubmissionExperience.MEMES_LEGACY && <MobileMemesArtSubmissionBtn wave={wave} isSubmissionLocked={isVotingControlsLocked} />;
    $[75] = isVotingControlsLocked;
    $[76] = submissionExperience;
    $[77] = wave;
    $[78] = t25;
  } else {
    t25 = $[78];
  }
  let t26;
  if ($[79] !== onContainerDrop || $[80] !== t22 || $[81] !== t23 || $[82] !== t24 || $[83] !== t25 || $[84] !== waveViewStyle) {
    t26 = <section className="tw-w-full tw-flex tw-flex-col tw-overflow-y-auto tw-overflow-x-hidden lg:tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 desktop-hover:hover:tw-scrollbar-thumb-iron-300 scroll-shadow tw-flex-grow tw-relative" style={waveViewStyle} aria-label="Wave chat file upload area" onDragEnter={onContainerDragEnter} onDragOver={onContainerDragOver} onDragLeave={onContainerDragLeave} onDrop={onContainerDrop}>{t22}{t23}{t24}{t25}</section>;
    $[79] = onContainerDrop;
    $[80] = t22;
    $[81] = t23;
    $[82] = t24;
    $[83] = t25;
    $[84] = waveViewStyle;
    $[85] = t26;
  } else {
    t26 = $[85];
  }
  let t27;
  if ($[86] !== t18 || $[87] !== t19 || $[88] !== t21 || $[89] !== t26) {
    t27 = <UnreadDividerProvider initialSerialNo={t18} key={t19}>{t21}{t26}</UnreadDividerProvider>;
    $[86] = t18;
    $[87] = t19;
    $[88] = t21;
    $[89] = t26;
    $[90] = t27;
  } else {
    t27 = $[90];
  }
  return t27;
};
export default MyStreamWaveChat;
function _temp(event) {
  const target = event.target;
  if (!(target instanceof Element)) {
    return true;
  }
  return target.closest("[contenteditable=\"true\"], input, textarea, [role=\"textbox\"]") === null;
}
function _temp2(event_0) {
  return Array.from(event_0.dataTransfer.types).includes("Files");
}
function _temp3(files) {
  const supported = [];
  const unsupported = [];
  files.forEach(file => {
    if (isSupportedUploadFile(file)) {
      supported.push(file);
    } else {
      unsupported.push(file);
    }
  });
  return {
    supported,
    unsupported
  };
}
function _temp4(file_0) {
  return file_0.name;
}
function _temp5(unsupported_0) {
  const displayedNames = unsupported_0.slice(0, MAX_UNSUPPORTED_FILE_NAMES_IN_TOAST).map(_temp4);
  const remainingCount = Math.max(0, unsupported_0.length - displayedNames.length);
  return remainingCount > 0 ? `${displayedNames.join(", ")} and ${remainingCount} more` : displayedNames.join(", ");
}